/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\directives\permission.ts
 * 权限控制指令
 */

import type { App, Directive, DirectiveBinding } from 'vue'
import { useUserStore } from '../stores/user'

interface PermissionBinding {
  value: string | string[]
  modifiers?: {
    all?: boolean  // 需要所有权限
    hide?: boolean // 隐藏元素（默认行为）
    disable?: boolean // 禁用元素
  }
}

/**
 * 权限检查函数
 * @param permissions - 需要检查的权限
 * @param requireAll - 是否需要所有权限
 * @returns 是否有权限
 */
function hasPermission(permissions: string | string[], requireAll: boolean = false): boolean {
  const userStore = useUserStore()
  
  if (!userStore.currentUser || !userStore.currentUser.permissions) {
    return false
  }
  
  // 超级用户拥有所有权限
  if (userStore.currentUser.is_superuser) {
    return true
  }
  
  const permissionList = Array.isArray(permissions) ? permissions : [permissions]
  
  if (requireAll) {
    return userStore.hasAllPermissions(permissionList)
  } else {
    return userStore.hasAnyPermission(permissionList)
  }
}

/**
 * 权限指令
 */
const permissionDirective: Directive<HTMLElement, PermissionBinding['value']> = {
  mounted(el, binding: DirectiveBinding<PermissionBinding['value']>) {
    updateElement(el, binding)
  },
  
  updated(el, binding: DirectiveBinding<PermissionBinding['value']>) {
    updateElement(el, binding)
  }
}

/**
 * 更新元素状态
 * @param el - DOM元素
 * @param binding - 指令绑定
 */
function updateElement(el: HTMLElement, binding: DirectiveBinding<PermissionBinding['value']>) {
  const { value, modifiers = {} } = binding
  
  if (!value) {
    console.warn('v-permission directive requires a value')
    return
  }
  
  const hasAccess = hasPermission(value, modifiers.all)
  
  if (!hasAccess) {
    if (modifiers.disable) {
      // 禁用元素
      el.style.pointerEvents = 'none'
      el.style.opacity = '0.5'
      el.setAttribute('disabled', 'true')
      
      // 如果是按钮元素，添加disabled属性
      if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
        (el as HTMLButtonElement | HTMLInputElement).disabled = true
      }
    } else {
      // 隐藏元素（默认行为）
      el.style.display = 'none'
    }
  } else {
    // 恢复元素状态
    el.style.display = ''
    el.style.pointerEvents = ''
    el.style.opacity = ''
    el.removeAttribute('disabled')
    
    // 如果是按钮元素，移除disabled属性
    if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
      (el as HTMLButtonElement | HTMLInputElement).disabled = false
    }
  }
}

/**
 * 角色检查函数
 * @param roles - 需要检查的角色
 * @returns 是否有角色权限
 */
function hasRole(roles: string | string[]): boolean {
  const userStore = useUserStore()
  
  if (!userStore.currentUser) {
    return false
  }
  
  // 超级用户拥有所有角色权限
  if (userStore.currentUser.is_superuser) {
    return true
  }
  
  const roleList = Array.isArray(roles) ? roles : [roles]
  const userRole = userStore.userRole
  
  return roleList.includes(userRole || '')
}

/**
 * 角色指令
 */
const roleDirective: Directive<HTMLElement, string | string[]> = {
  mounted(el, binding: DirectiveBinding<string | string[]>) {
    updateRoleElement(el, binding)
  },
  
  updated(el, binding: DirectiveBinding<string | string[]>) {
    updateRoleElement(el, binding)
  }
}

/**
 * 更新角色元素状态
 * @param el - DOM元素
 * @param binding - 指令绑定
 */
function updateRoleElement(el: HTMLElement, binding: DirectiveBinding<string | string[]>) {
  const { value, modifiers = {} } = binding
  
  if (!value) {
    console.warn('v-role directive requires a value')
    return
  }
  
  const hasAccess = hasRole(value)
  
  if (!hasAccess) {
    if (modifiers.disable) {
      // 禁用元素
      el.style.pointerEvents = 'none'
      el.style.opacity = '0.5'
      el.setAttribute('disabled', 'true')
      
      if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
        (el as HTMLButtonElement | HTMLInputElement).disabled = true
      }
    } else {
      // 隐藏元素（默认行为）
      el.style.display = 'none'
    }
  } else {
    // 恢复元素状态
    el.style.display = ''
    el.style.pointerEvents = ''
    el.style.opacity = ''
    el.removeAttribute('disabled')
    
    if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
      (el as HTMLButtonElement | HTMLInputElement).disabled = false
    }
  }
}

/**
 * 安装权限指令
 * @param app - Vue应用实例
 */
export function installPermissionDirectives(app: App) {
  // 权限指令
  app.directive('permission', permissionDirective)
  
  // 角色指令
  app.directive('role', roleDirective)
}

// 导出指令
export { permissionDirective, roleDirective }

// 导出权限检查函数供其他地方使用
export { hasPermission, hasRole }